import { logger } from "utils/logger";
import React, { useEffect, useRef, useState } from 'react'
import Hls from 'hls.js'
import Cookies from 'js-cookie'
import { VideoPlayerContainer, VideoWrap, VideoTitle } from './VideoPlayer.styles'

const BASE_URL = process.env.REACT_APP_BASE_URL || ''

interface VideoPlayerProps {
  lessonId: number
  videoId: number
  courseId: number
  videoName?: string
  onVideoRef?: (ref: HTMLVideoElement | null) => void
  onPlay?: () => void
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ lessonId, videoId, courseId, videoName, onVideoRef, onPlay }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const playerRef = useRef<HTMLDivElement | null>(null)
  const videoWrapRef = useRef<HTMLDivElement | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showTitle, setShowTitle] = useState(false)
  const hideTitleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Формируем URL, избегая двойных слэшей
  // BASE_URL уже содержит /api/v1
  const baseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL
  const playlistUrl = `${baseUrl}/videos/lesson/${lessonId}/video/${videoId}/hls/playlist.m3u8`

  useEffect(() => {
    const video = videoRef.current
    if (!video || !playlistUrl) {
      logger.error('VideoPlayer: video element or playlistUrl отсутствует', { video: !!video, playlistUrl })
      return
    }

    const accessToken = Cookies.get("access_token")
    if (!accessToken) {
      logger.error('VideoPlayer: Токен авторизации отсутствует')
      setError('Токен авторизации отсутствует')
      return
    }

    logger.log('VideoPlayer: Инициализация HLS', { 
      playlistUrl, 
      lessonId, 
      videoId, 
      courseId,
      hasToken: !!accessToken,
      hlsSupported: Hls.isSupported()
    })

    if (Hls.isSupported()) {
      const hls = new Hls({
        xhrSetup: (xhr, url) => {
          xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`)
          logger.log('VideoPlayer: XHR запрос', { url, hasAuth: !!accessToken })
        },
      })
      
      hls.attachMedia(video)
      
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        logger.log('VideoPlayer: Медиа прикреплено, загрузка источника:', playlistUrl)
        hls.loadSource(playlistUrl)
        // Обновляем ref после прикрепления медиа
        if (onVideoRef && videoRef.current) {
          onVideoRef(videoRef.current)
        }
      })
      
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        logger.log('VideoPlayer: Манифест загружен успешно')
      })
      
      hls.on(Hls.Events.ERROR, (event, data) => {
        logger.error('VideoPlayer: HLS ошибка:', {
          type: data.type,
          details: data.details,
          fatal: data.fatal,
          error: data.error,
          response: data.response,
          url: data.url,
        })
        
        if (data?.fatal) {
          let errorMessage = 'Ошибка воспроизведения видео'
          let shouldDestroy = false
          
          // Более детальные сообщения об ошибках
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            errorMessage = `Сетевая ошибка: ${data.details || 'Не удалось загрузить видео'}`
            logger.log('VideoPlayer: Попытка перезагрузки при сетевой ошибке')
            hls.startLoad()
            return // Не показываем ошибку сразу, ждем результат перезагрузки
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            errorMessage = `Ошибка медиа: ${data.details || 'Ошибка воспроизведения'}`
            logger.log('VideoPlayer: Попытка восстановления при ошибке медиа')
            hls.recoverMediaError()
            return // Не показываем ошибку сразу, ждем результат восстановления
          } else if (data.response?.code === 401) {
            errorMessage = 'Ошибка авторизации. Проверьте токен доступа.'
            shouldDestroy = true
          } else if (data.response?.code === 403) {
            errorMessage = 'Доступ запрещен. Проверьте права доступа к курсу.'
            shouldDestroy = true
          } else if (data.response?.code === 404) {
            errorMessage = 'Видео не найдено.'
            shouldDestroy = true
          } else if (data.response?.code) {
            errorMessage = `Ошибка ${data.response.code}: ${data.details || 'Ошибка загрузки видео'}`
            shouldDestroy = true
          } else {
            // Для других типов критических ошибок
            errorMessage = `Критическая ошибка: ${data.details || 'Неизвестная ошибка'}`
            shouldDestroy = true
          }
          
          setError(errorMessage)
          
          // Критическая ошибка - уничтожаем HLS
          if (shouldDestroy) {
            logger.error('VideoPlayer: Критическая ошибка, уничтожение HLS')
            hls.destroy()
          }
        }
      })
      
      return () => {
        logger.log('VideoPlayer: Очистка HLS')
        hls.destroy()
      }
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Нативный HLS (Safari). ВНИМАНИЕ: заголовки на сегменты не добавить —
      // поэтому Bearer токен не сработает. Используем только для тестирования.
      logger.warn('VideoPlayer: Используется нативный HLS (Safari), Bearer токен может не работать')
      video.src = playlistUrl
      video.addEventListener('error', (e) => {
        logger.error('VideoPlayer: Ошибка нативного видео:', e)
        setError('Ошибка воспроизведения (нативный HLS не поддерживает Bearer токен)')
      })
      return () => {
        video.removeAttribute('src')
        video.load()
      }
    } else {
      logger.error('VideoPlayer: HLS не поддерживается')
      setError('HLS не поддерживается вашим браузером')
    }
  }, [playlistUrl, lessonId, videoId, courseId])

  // Логика показа/скрытия названия при наведении и движении мыши
  useEffect(() => {
    const videoWrap = videoWrapRef.current
    if (!videoWrap || !videoName) return

    const resetHideTimer = () => {
      if (hideTitleTimerRef.current) {
        clearTimeout(hideTitleTimerRef.current)
      }
      setShowTitle(true)
      hideTitleTimerRef.current = setTimeout(() => {
        setShowTitle(false)
      }, 3000)
    }

    const handleMouseEnter = () => {
      setShowTitle(true)
      resetHideTimer()
    }

    const handleMouseMove = () => {
      resetHideTimer()
    }

    const handleMouseLeave = () => {
      if (hideTitleTimerRef.current) {
        clearTimeout(hideTitleTimerRef.current)
      }
      setShowTitle(false)
    }

    videoWrap.addEventListener('mouseenter', handleMouseEnter)
    videoWrap.addEventListener('mousemove', handleMouseMove)
    videoWrap.addEventListener('mouseleave', handleMouseLeave)

    return () => {
      videoWrap.removeEventListener('mouseenter', handleMouseEnter)
      videoWrap.removeEventListener('mousemove', handleMouseMove)
      videoWrap.removeEventListener('mouseleave', handleMouseLeave)
      if (hideTitleTimerRef.current) {
        clearTimeout(hideTitleTimerRef.current)
      }
    }
  }, [videoName])

  // Передаем ref видео элемента в родительский компонент
  useEffect(() => {
    if (!onVideoRef) return
    
    // Используем небольшую задержку для гарантии, что video элемент уже создан
    const updateRef = () => {
      if (videoRef.current) {
        onVideoRef(videoRef.current)
      }
    }
    
    // Проверяем сразу и через небольшую задержку
    updateRef()
    const timeoutId = setTimeout(updateRef, 100)
    
    return () => {
      clearTimeout(timeoutId)
      if (onVideoRef) {
        onVideoRef(null)
      }
    }
  }, [onVideoRef, playlistUrl]) // Добавляем playlistUrl чтобы обновить ref после инициализации HLS

  // Обработчик события play для уведомления родителя
  useEffect(() => {
    const video = videoRef.current
    if (!video || !onPlay) return

    const handlePlay = () => {
      // Используем небольшую задержку чтобы убедиться что видео действительно начало воспроизводиться
      setTimeout(() => {
        if (!video.paused) {
          onPlay()
        }
      }, 50)
    }

    video.addEventListener('play', handlePlay)
    // Также слушаем событие playing для большей надежности
    video.addEventListener('playing', handlePlay)
    
    return () => {
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('playing', handlePlay)
    }
  }, [onPlay])

  // Определяем, является ли устройство мобильным для правильной настройки playsInline
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

  if (error) {
    return (
      <VideoPlayerContainer>
        <div style={{ padding: 16, color: '#fff', textAlign: 'center' }}>
          {error}
        </div>
      </VideoPlayerContainer>
    )
  }

  return (
    <VideoPlayerContainer ref={playerRef}>
      <VideoWrap 
        ref={videoWrapRef}
        onContextMenu={(e) => e.preventDefault()}
      >
        <video
          ref={videoRef}
          controls
          playsInline={isMobile}
          preload="metadata"
          controlsList="nodownload noremoteplayback"
        />
        {videoName && (
          <VideoTitle $show={showTitle}>
            {videoName}
          </VideoTitle>
        )}
      </VideoWrap>
    </VideoPlayerContainer>
  )
}

export default VideoPlayer
